import React, { useState, useEffect } from "react";
import "../styles/Quiz.css";
import "../styles/FilterModal.css";

const FilterModal = ({ onClose, onApply, initialFilters = [] }) => {
  // State for active tab
  const [activeTab, setActiveTab] = useState("restrictions");
  
  // State for selected filters
  const [selectedFilters, setSelectedFilters] = useState([]);
  
  // Initialize with any existing filters
  useEffect(() => {
    setSelectedFilters(initialFilters);
  }, [initialFilters]);

  // Handle checkbox changes for regular filters (allergies and diet restrictions)
  const handleRegularCheckboxChange = (event) => {
    const value = event.target.value;
    
    setSelectedFilters((prevFilters) => {
      if (event.target.checked) {
        return [...prevFilters, value];
      } else {
        return prevFilters.filter((filter) => filter !== value);
      }
    });
  };

  // Handle radio-like behavior for Spice and Cuisine
  const handleExclusiveSelection = (value, category) => {
    setSelectedFilters((prevFilters) => {
      // If clicking the same value that's already selected, just remove it (toggle off)
      if (prevFilters.includes(value)) {
        return prevFilters.filter(filter => filter !== value);
      }
      
      // Remove any existing spice levels if this is a spice selection
      // or remove any existing cuisine types if this is a cuisine selection
      const filteredSelections = prevFilters.filter(filter => {
        if (category === 'spice') {
          return !['No Spice', 'Mild', 'Medium', 'Hot', 'Extreme'].includes(filter);
        } else if (category === 'cuisine') {
          return !['Mexican', 'Chinese', 'Japanese', 'Korean', 'Indian', 'American', 'Italian'].includes(filter);
        }
        return true;
      });
      
      // Add the new value
      return [...filteredSelections, value];
    });
  };

  // Apply filters and close modal
  const handleApplyFilters = () => {
    // Normalize filter terms to match the format used in dishes2.json
    const normalizedFilters = selectedFilters.map(filter => {
      // Special case transformations
      if (filter.toLowerCase() === "no spice") return "No Spice";
      if (filter.toLowerCase() === "gluten free") return "Gluten Free";
      if (filter.toLowerCase() === "soy free") return "Soy Free";
      if (filter.toLowerCase() === "dairy free") return "Dairy Free";
      if (filter.toLowerCase() === "lactose free") return "Lactose Free";
      if (filter.toLowerCase() === "nut free") return "Nut Free";
      if (filter.toLowerCase() === "shellfish free") return "Shellfish Free";
      if (filter.toLowerCase() === "seafood free") return "Seafood Free";
      if (filter.toLowerCase() === "wheat free") return "Wheat Free";
      if (filter.toLowerCase() === "vegetarian") return "Vegetarian";
      if (filter.toLowerCase() === "vegan") return "Vegan";
      if (filter.toLowerCase() === "pescatarian") return "Pescatarian";
      if (filter.toLowerCase() === "hot") return "Hot";
      if (filter.toLowerCase() === "mild") return "Mild";
      if (filter.toLowerCase() === "medium") return "Medium";
      if (filter.toLowerCase() === "extreme") return "Extreme";
      // Just return the original filter if no special transformation needed
      return filter;
    });

    onApply(normalizedFilters);
    onClose();
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSelectedFilters([]);
  };

  // Check if a filter is selected
  const isFilterSelected = (value) => {
    return selectedFilters.includes(value);
  };

  // Stop propagation on modal click to prevent closing when clicking inside
  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  return (
    <div className="filter-modal-overlay" onClick={onClose}>
      <div className="filter-modal-content" onClick={handleModalClick}>
        <div className="filter-modal-header">
          <h2>Filter Posts</h2>
          <button className="filter-modal-close" onClick={onClose}>×</button>
        </div>
        
        {/* Tab Navigation */}
        <div className="filter-tabs">
          <button 
            className={`filter-tab ${activeTab === "restrictions" ? "active" : ""}`}
            onClick={() => setActiveTab("restrictions")}
          >
            Food & Diet Restrictions
          </button>
          <button 
            className={`filter-tab ${activeTab === "spice" ? "active" : ""}`}
            onClick={() => setActiveTab("spice")}
          >
            Spice & Cuisine
          </button>
        </div>
        
        <div className="filter-modal-body">
          {/* Food & Diet Restrictions Tab */}
          {activeTab === "restrictions" && (
            <div className="filter-section">
              <div className="filter-category">
                <div className="align">Food Allergy/Intolerance</div>
                <hr className="line"></hr>
                
                <div className="filter-option-container">
                  <label className="filter-option">
                    <input 
                      type="checkbox" 
                      value="Nut Free" 
                      onChange={handleRegularCheckboxChange}
                      checked={isFilterSelected("Nut Free")}
                    />
                    <span className="check">Nut Free</span>
                  </label>
                </div>
                
                <div className="filter-option-container">
                  <label className="filter-option">
                    <input 
                      type="checkbox" 
                      value="Shellfish Free" 
                      onChange={handleRegularCheckboxChange}
                      checked={isFilterSelected("Shellfish Free")}
                    />
                    <span className="check">Shellfish Free</span>
                  </label>
                </div>
                
                <div className="filter-option-container">
                  <label className="filter-option">
                    <input 
                      type="checkbox" 
                      value="Seafood Free" 
                      onChange={handleRegularCheckboxChange}
                      checked={isFilterSelected("Seafood Free")}
                    />
                    <span className="check">Seafood Free</span>
                  </label>
                </div>
                
                <div className="filter-option-container">
                  <label className="filter-option">
                    <input 
                      type="checkbox" 
                      value="Wheat Free" 
                      onChange={handleRegularCheckboxChange}
                      checked={isFilterSelected("Wheat Free")}
                    />
                    <span className="check">Wheat Free</span>
                  </label>
                </div>
                
                <div className="filter-option-container">
                  <label className="filter-option">
                    <input 
                      type="checkbox" 
                      value="Soy Free" 
                      onChange={handleRegularCheckboxChange}
                      checked={isFilterSelected("Soy Free")}
                    />
                    <span className="check">Soy Free</span>
                  </label>
                </div>
                
                <div className="filter-option-container">
                  <label className="filter-option">
                    <input 
                      type="checkbox" 
                      value="Dairy Free" 
                      onChange={handleRegularCheckboxChange}
                      checked={isFilterSelected("Dairy Free")}
                    />
                    <span className="check">Dairy Free</span>
                  </label>
                </div>
                
                <div className="filter-option-container">
                  <label className="filter-option">
                    <input 
                      type="checkbox" 
                      value="Gluten Free" 
                      onChange={handleRegularCheckboxChange}
                      checked={isFilterSelected("Gluten Free")}
                    />
                    <span className="check">Gluten Free</span>
                  </label>
                </div>
                
                <div className="filter-option-container">
                  <label className="filter-option">
                    <input 
                      type="checkbox" 
                      value="Lactose Free" 
                      onChange={handleRegularCheckboxChange}
                      checked={isFilterSelected("Lactose Free")}
                    />
                    <span className="check">Lactose Free</span>
                  </label>
                </div>
              </div>
              
              <div className="filter-category">
                <div className="align">Diet Restrictions</div>
                <hr className="line"></hr>
                
                <div className="filter-option-container">
                  <label className="filter-option">
                    <input 
                      type="checkbox" 
                      value="Vegan" 
                      onChange={handleRegularCheckboxChange}
                      checked={isFilterSelected("Vegan")}
                    />
                    <span className="check">Vegan</span>
                  </label>
                </div>
                
                <div className="filter-option-container">
                  <label className="filter-option">
                    <input 
                      type="checkbox" 
                      value="Vegetarian" 
                      onChange={handleRegularCheckboxChange}
                      checked={isFilterSelected("Vegetarian")}
                    />
                    <span className="check">Vegetarian</span>
                  </label>
                </div>
                
                <div className="filter-option-container">
                  <label className="filter-option">
                    <input 
                      type="checkbox" 
                      value="Pescatarian" 
                      onChange={handleRegularCheckboxChange}
                      checked={isFilterSelected("Pescatarian")}
                    />
                    <span className="check">Pescatarian</span>
                  </label>
                </div>
                
                <div className="filter-option-container">
                  <label className="filter-option">
                    <input 
                      type="checkbox" 
                      value="Halal" 
                      onChange={handleRegularCheckboxChange}
                      checked={isFilterSelected("Halal")}
                    />
                    <span className="check">Halal</span>
                  </label>
                </div>
                
                <div className="filter-option-container">
                  <label className="filter-option">
                    <input 
                      type="checkbox" 
                      value="Kosher" 
                      onChange={handleRegularCheckboxChange}
                      checked={isFilterSelected("Kosher")}
                    />
                    <span className="check">Kosher</span>
                  </label>
                </div>
              </div>
            </div>
          )}
          
          {/* Spice and Cuisine Tab */}
          {activeTab === "spice" && (
            <div className="filter-section">
              <div className="filter-category">
                <div className="align">Spice Tolerance</div>
                <hr className="line"></hr>
                
                <div className="filter-option-container">
                  <label className="filter-option">
                    <input 
                      type="checkbox"
                      value="No Spice" 
                      onChange={() => handleExclusiveSelection("No Spice", "spice")}
                      checked={isFilterSelected("No Spice")}
                    />
                    <span className="check">No Spice</span>
                  </label>
                </div>
                
                <div className="filter-option-container">
                  <label className="filter-option">
                    <input 
                      type="checkbox"
                      value="Mild" 
                      onChange={() => handleExclusiveSelection("Mild", "spice")}
                      checked={isFilterSelected("Mild")}
                    />
                    <span className="check">Mild</span>
                  </label>
                </div>
                
                <div className="filter-option-container">
                  <label className="filter-option">
                    <input 
                      type="checkbox"
                      value="Medium" 
                      onChange={() => handleExclusiveSelection("Medium", "spice")}
                      checked={isFilterSelected("Medium")}
                    />
                    <span className="check">Medium</span>
                  </label>
                </div>
                
                <div className="filter-option-container">
                  <label className="filter-option">
                    <input 
                      type="checkbox"
                      value="Hot" 
                      onChange={() => handleExclusiveSelection("Hot", "spice")}
                      checked={isFilterSelected("Hot")}
                    />
                    <span className="check">Hot</span>
                  </label>
                </div>
                
                <div className="filter-option-container">
                  <label className="filter-option">
                    <input 
                      type="checkbox"
                      value="Extreme" 
                      onChange={() => handleExclusiveSelection("Extreme", "spice")}
                      checked={isFilterSelected("Extreme")}
                    />
                    <span className="check">Extreme</span>
                  </label>
                </div>
              </div>
              
              <div className="filter-category">
                <div className="align">Cuisine Preference</div>
                <hr className="line"></hr>
                
                <div className="filter-option-container">
                  <label className="filter-option">
                    <input 
                      type="checkbox"
                      value="Mexican" 
                      onChange={() => handleExclusiveSelection("Mexican", "cuisine")}
                      checked={isFilterSelected("Mexican")}
                    />
                    <span className="check">Mexican</span>
                  </label>
                </div>
                
                <div className="filter-option-container">
                  <label className="filter-option">
                    <input 
                      type="checkbox"
                      value="Chinese" 
                      onChange={() => handleExclusiveSelection("Chinese", "cuisine")}
                      checked={isFilterSelected("Chinese")}
                    />
                    <span className="check">Chinese</span>
                  </label>
                </div>
                
                <div className="filter-option-container">
                  <label className="filter-option">
                    <input 
                      type="checkbox"
                      value="Japanese" 
                      onChange={() => handleExclusiveSelection("Japanese", "cuisine")}
                      checked={isFilterSelected("Japanese")}
                    />
                    <span className="check">Japanese</span>
                  </label>
                </div>
                
                <div className="filter-option-container">
                  <label className="filter-option">
                    <input 
                      type="checkbox"
                      value="Korean" 
                      onChange={() => handleExclusiveSelection("Korean", "cuisine")}
                      checked={isFilterSelected("Korean")}
                    />
                    <span className="check">Korean</span>
                  </label>
                </div>
                
                <div className="filter-option-container">
                  <label className="filter-option">
                    <input 
                      type="checkbox"
                      value="Indian" 
                      onChange={() => handleExclusiveSelection("Indian", "cuisine")}
                      checked={isFilterSelected("Indian")}
                    />
                    <span className="check">Indian</span>
                  </label>
                </div>
                
                <div className="filter-option-container">
                  <label className="filter-option">
                    <input 
                      type="checkbox"
                      value="American" 
                      onChange={() => handleExclusiveSelection("American", "cuisine")}
                      checked={isFilterSelected("American")}
                    />
                    <span className="check">American</span>
                  </label>
                </div>
                
                <div className="filter-option-container">
                  <label className="filter-option">
                    <input 
                      type="checkbox"
                      value="Italian" 
                      onChange={() => handleExclusiveSelection("Italian", "cuisine")}
                      checked={isFilterSelected("Italian")}
                    />
                    <span className="check">Italian</span>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Footer with action buttons */}
        <div className="filter-modal-footer">
          <div className="filter-selection-summary">
            {selectedFilters.length > 0 ? (
              <span>{selectedFilters.length} filters selected</span>
            ) : (
              <span>No filters selected</span>
            )}
          </div>
          <div className="filter-actions">
            <button 
              className="filter-clear-button" 
              onClick={handleClearFilters}
              disabled={selectedFilters.length === 0}
            >
              Clear All
            </button>
            <button className="filter-apply-button" onClick={handleApplyFilters}>
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterModal; 